'use client';

/**
 * API namespace `landing`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L5265-L5443).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import { ApiClientError } from '../core/error-handler';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const landing = {
  /**
   * GET /api/landing — list user's landings + limits metadata.
   *
   * SPEC-060 (2026-08-22): the backend now returns `{ landings, limits }`
   * where `limits` is `{ tier, starCount, maxLandings, currentCount, canCreate }`.
   * The frontend uses `limits.canCreate` to enable/disable the "Crear nueva
   * landing" button + show "2/3 landings" count in the editor header.
   */
  async list(signal?: AbortSignal): Promise<{ landings: Landing[]; limits: LandingLimits | null }> {
    // SPEC-060 fix: apiFetch already extracts body.data, so the generic
    // type is the INNER shape (not wrapped in { data: ... }).
    // Pre-fix used `apiFetch<{ data: Landing[] }>` which returned undefined
    // at runtime (handleResponse extracts .data, so `res.data` was undefined).
    const res = await apiFetch<{ landings: Landing[]; limits: LandingLimits | null }>('/landing', { signal });
    return res ?? { landings: [], limits: null };
  },
  /**
   * GET /api/landing/:slug — fetch by slug.
   * For slug='main', the backend auto-creates the landing if it doesn't
   * exist yet (the editor's initial-load path).
   */
  async get(slug: string, signal?: AbortSignal): Promise<Landing> {
    // SPEC-060 fix: apiFetch already extracts body.data, so the generic
    // type is `Landing` (not `{ data: Landing }`).
    return apiFetch<Landing>(
      `/landing/${encodeURIComponent(slug)}`,
      { signal },
    );
  },
  /** POST /api/landing — create new landing (max per tier per SPEC-060). */
  async create(body: {
    slug?: string;
    title?: string;
    description?: string;
    blocks?: Landing['blocks'];
    theme?: Landing['theme'];
  }): Promise<Landing> {
    return apiFetch<Landing>('/landing', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  /** PUT /api/landing/:slug — update landing (blocks, theme, title). */
  async update(
    slug: string,
    body: {
      title?: string;
      description?: string;
      blocks?: Landing['blocks'];
      theme?: Landing['theme'];
    },
  ): Promise<Landing> {
    return apiFetch<Landing>(
      `/landing/${encodeURIComponent(slug)}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    );
  },
  /** DELETE /api/landing/:slug — delete landing (returns 204). */
  async remove(slug: string): Promise<void> {
    await apiFetch<void>(`/landing/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
    });
  },
  /** POST /api/landing/:slug/publish — set status=published. */
  async publish(slug: string): Promise<Landing> {
    return apiFetch<Landing>(
      `/landing/${encodeURIComponent(slug)}/publish`,
      { method: 'POST' },
    );
  },
  /** POST /api/landing/:slug/unpublish — set status=draft. */
  async unpublish(slug: string): Promise<Landing> {
    return apiFetch<Landing>(
      `/landing/${encodeURIComponent(slug)}/unpublish`,
      { method: 'POST' },
    );
  },
  /**
   * SPEC-173 (Sprint 85): POST /api/landing/purchase-extra — purchase
   * 1 extra landing slot for 10 créditos PM. Valid for 365 days.
   */
  async purchaseExtra(): Promise<{ landingExtras: { count: number; expiresAt: string }; pricePaid: string }> {
    return apiFetch('/landing/purchase-extra', { method: 'POST' });
  },
  // SPEC-242 F3: Custom Domain endpoints (Oro+).
  // POST /api/landing/:slug/custom-domain — set domain + get verification token.
  async setCustomDomain(slug: string, domain: string): Promise<Landing> {
    return apiFetch<Landing>(
      `/landing/${encodeURIComponent(slug)}/custom-domain`,
      { method: 'POST', body: JSON.stringify({ domain }) },
    );
  },
  // POST /api/landing/:slug/custom-domain/verify — verify DNS TXT record.
  async verifyCustomDomain(slug: string): Promise<Landing> {
    return apiFetch<Landing>(
      `/landing/${encodeURIComponent(slug)}/custom-domain/verify`,
      { method: 'POST' },
    );
  },
  // DELETE /api/landing/:slug/custom-domain — remove custom domain.
  async removeCustomDomain(slug: string): Promise<Landing> {
    return apiFetch<Landing>(
      `/landing/${encodeURIComponent(slug)}/custom-domain`,
      { method: 'DELETE' },
    );
  },
  // SPEC-243: POST /api/landing/:slug/renew — renew landing expiry (10 créditos PM).
  async renewLanding(slug: string): Promise<Landing> {
    return apiFetch<Landing>(
      `/landing/${encodeURIComponent(slug)}/renew`,
      { method: 'POST' },
    );
  },
  /**
   * GET /api/landing/public/:username/:slug — PUBLIC (no auth).
   *
   * Used by the SSR public route `/l/[username]/page.tsx` to render
   * a published landing server-side. Returns null if not found /
   * not published (the caller turns this into a 404).
   *
   * CM-19 (single source of truth): the response shape matches the
   * `Landing` type in landing-types.ts — no separate "public" type.
   */
  async getPublic(
    username: string,
    slug: string,
    signal?: AbortSignal,
  ): Promise<Landing | null> {
    try {
      return await apiFetch<Landing>(
        `/landing/public/${encodeURIComponent(username)}/${encodeURIComponent(slug)}`,
        { auth: false, skipRefresh: true, signal },
      );
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) return null;
      throw err;
    }
  },
};
